import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import "./Dashboard.css";

/* ── Sous-composants internes ── */
function LastCycleBanner({ data, t }) {
  const dayShort = t("cycleBot.common.dayShort");
  return (
    <div className="cb-last-cycle-banner">
      <div className="cb-lcb-item">
        <span className="cb-lcb-icon">📅</span>
        <div>
          <span className="cb-lcb-val">{data?.j1 || "--"}</span>
          <span className="cb-lcb-lbl">{t("cycleBot.dashboard.lastJ1")}</span>
        </div>
      </div>
      <div className="cb-lcb-item">
        <span className="cb-lcb-icon">🩸</span>
        <div>
          <span className="cb-lcb-val">{data?.bleedDays ? `${data.bleedDays}${dayShort}` : "--"}</span>
          <span className="cb-lcb-lbl">{t("cycleBot.dashboard.bleeding")}</span>
        </div>
      </div>
      <div className="cb-lcb-item">
        <span className="cb-lcb-icon">📏</span>
        <div>
          <span className="cb-lcb-val">{data?.cycleLen ? `${data.cycleLen}${dayShort}` : "--"}</span>
          <span className="cb-lcb-lbl">{t("cycleBot.dashboard.cycleLength")}</span>
        </div>
      </div>
      <div className="cb-lcb-item">
        <span className="cb-lcb-icon">📝</span>
        <div>
          <span className="cb-lcb-val cb-lcb-notes">{data?.notes || "--"}</span>
          <span className="cb-lcb-lbl">{t("cycleBot.dashboard.medicalNote")}</span>
        </div>
      </div>
    </div>
  );
}

function CountdownCard({ daysLeft, nextDate, progress, t }) {
  return (
    <div className="cb-card cb-card-g1">
      <div className="cb-card-hd">
        <span className="cb-card-icon">📅</span>
        <h3>{t("cycleBot.dashboard.nextEstimatedJ1")}</h3>
      </div>
      <div className="cb-countdown">
        <span className="cb-days-num">{daysLeft ?? "--"}</span>
        <span className="cb-days-label">{t("cycleBot.dashboard.daysRemaining")}</span>
      </div>
      <div className="cb-card-date">{nextDate || t("cycleBot.dashboard.calculating")}</div>
      <div className="cb-prog-bar-wrap">
        <div className="cb-prog-bar">
          <div
            className="cb-prog-fill"
            style={{ width: `${progress || 0}%` }}
          />
        </div>
      </div>
    </div>
  );
}

function FertilityCard({ period, status, chance, t }) {
  return (
    <div className="cb-card cb-card-g2">
      <div className="cb-card-hd">
        <span className="cb-card-icon">🌸</span>
        <h3>{t("cycleBot.dashboard.fertilityWindow")}</h3>
      </div>
      <div className="cb-fertil-dates">{period || "--"}</div>
      <div className="cb-fertil-badge">
        <span className="cb-fertil-dot" />
        <span>{status || "--"}</span>
      </div>
      {chance && <div className="cb-fertil-chance">{chance}</div>}
    </div>
  );
}

function ScoreCard({ score, message, t }) {
  const circumference = 314;
  const offset = score != null
    ? circumference - (score / 100) * circumference
    : circumference;

  return (
    <div className="cb-card cb-card-g3">
      <div className="cb-card-hd">
        <span className="cb-card-icon">💯</span>
        <h3>{t("cycleBot.dashboard.healthScore")}</h3>
      </div>
      <div className="cb-score-wrap">
        <svg className="cb-score-svg" viewBox="0 0 120 120">
          <circle className="cb-score-track" cx="60" cy="60" r="50" />
          <circle
            className="cb-score-ring"
            cx="60" cy="60" r="50"
            style={{ strokeDashoffset: offset }}
          />
        </svg>
        <div className="cb-score-center">
          <span className="cb-score-num">{score ?? "--"}</span>
          <span className="cb-score-over">/100</span>
        </div>
      </div>
      <div className="cb-score-msg">{message || t("cycleBot.dashboard.pending")}</div>
    </div>
  );
}

function StatsCard({ cyclesCount, avgCycle, alertsCount, regularity, t }) {
  const dayShort = t("cycleBot.common.dayShort");
  return (
    <div className="cb-card cb-card-white">
      <div className="cb-card-hd">
        <h3>{t("cycleBot.dashboard.stats3Months")}</h3>
      </div>
      <div className="cb-stats-grid">
        {[
          { icon: "📊", val: cyclesCount, lbl: t("cycleBot.dashboard.cycles") },
          { icon: "⏱️", val: avgCycle ? `${avgCycle}${dayShort}` : "--", lbl: t("cycleBot.dashboard.avgDuration") },
          { icon: "⚠️", val: alertsCount, lbl: t("cycleBot.dashboard.alerts") },
          { icon: "📈", val: regularity, lbl: t("cycleBot.dashboard.regularity") },
        ].map((s) => (
          <div key={s.lbl} className="cb-stat">
            <span className="cb-stat-icon">{s.icon}</span>
            <span className="cb-stat-val">{s.val ?? "--"}</span>
            <span className="cb-stat-lbl">{s.lbl}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function AlertCard({ alert }) {
  const isCrit = alert.level === "critique" || alert.level === "critical";
  return (
    <div className={`cb-alert-card ${isCrit ? "crit" : ""}`}>
      <div className="cb-alert-hd">
        <span className="cb-alert-icon">{isCrit ? "🚨" : "⚠️"}</span>
        <span className="cb-alert-title">{alert.message}</span>
        <span className="cb-alert-badge">{alert.level}</span>
      </div>
      {alert.details && (
        <div className="cb-alert-detail">{alert.details}</div>
      )}
      {alert.recommendations?.length > 0 && (
        <ul className="cb-alert-recs">
          {alert.recommendations.map((r, i) => (
            <li key={i}>{r}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* ── Composant principal ── */
export default function Dashboard({ data, onRefresh, premenstrualWindow, onGoJournal }) {
  const { t, i18n } = useTranslation("cycle");
  const locale = i18n.resolvedLanguage || i18n.language;
  const lastCycle = data?.last_cycle;
  const pred      = data?.predictions;
  const health    = data?.health_score;
  const stats     = data?.stats;
  const alerts    = data?.alerts || [];

  return (
    <div className="cb-view cb-view--active cb-dashboard-theme">
      {/* Header */}
      <div className="cb-view-head">
        <div>
          <h1>{t("cycleBot.dashboard.title")}</h1>
          <p className="cb-view-sub">{t("cycleBot.dashboard.subtitle")}</p>
        </div>
        <button className="cb-btn-icon" onClick={onRefresh} title={t("cycleBot.dashboard.refresh")}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <path
              d="M4 4v5h5M20 20v-5h-5M19.93 10A8 8 0 004.07 4.07M4.07 14A8 8 0 0019.93 19.93"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      {/* Dernier cycle banner */}
      <LastCycleBanner
        t={t}
        data={{
          j1:       lastCycle?.start_date,
          bleedDays: lastCycle?.bleeding_days,
          cycleLen: lastCycle?.cycle_length,
          notes:    lastCycle?.medical_note,
        }}
      />

      {/* Cards grid */}
      <div className="cb-dash-grid">
        <CountdownCard
          t={t}
          daysLeft={pred?.next_cycle?.daysUntil}
          nextDate={pred?.next_cycle?.start}
          progress={pred?.next_cycle?.progress}
        />
        <FertilityCard
          t={t}
          period={
            pred?.fertile_window
              ? `${pred.fertile_window.start} → ${pred.fertile_window.end}`
              : null
          }
          status={pred?.fertility_status}
          chance={pred?.pregnancy_chance}
        />
        <ScoreCard
          t={t}
          score={health?.score}
          message={health?.message}
        />
        <StatsCard
          t={t}
          cyclesCount={stats?.cycles_count}
          avgCycle={stats?.avg_cycle_length}
          alertsCount={stats?.alerts_count}
          regularity={stats?.regularity_score}
        />
      </div>

      {/* Alertes */}
      {/* ── Alerte pré-menstruelle ── */}
      {premenstrualWindow?.inWindow && (
        <div className="cb-premenstrual-alert cb-pma-clickable" onClick={onGoJournal} role="button" style={{ cursor: "pointer" }}>
          <div className="cb-pma-header">
            <span className="cb-pma-icon">🌙</span>
            <div className="cb-pma-titles">
              <div className="cb-pma-title">
                {t("cycleBot.dashboard.premenstrualWindowDay", { day: premenstrualWindow.dayInWindow })}
              </div>
              <div className="cb-pma-sub">
                {t("cycleBot.dashboard.nextPeriodIn", { count: premenstrualWindow.daysLeft })}{" "}
                ({new Date(premenstrualWindow.nextJ1Date).toLocaleDateString(locale, { day: "numeric", month: "long" })})
              </div>
            </div>
          </div>

          {/* Tracker 7 jours */}
          <div className="cb-pma-tracker">
            {[1,2,3,4,5,6,7].map((d) => (
              <div
                key={d}
                className={`cb-pma-dot ${
                  d < premenstrualWindow.dayInWindow ? "done"
                  : d === premenstrualWindow.dayInWindow ? "today"
                  : "pending"
                }`}
              >
                {d < premenstrualWindow.dayInWindow ? "✓" : d}
              </div>
            ))}
          </div>

          <div className="cb-pma-message">
            📝 {t("cycleBot.dashboard.fillJournalPrompt")}
          </div>
        </div>
      )}

      {premenstrualWindow?.reportDue && (
        <div className="cb-premenstrual-alert cb-pma-report-due">
          <span className="cb-pma-icon">🎯</span>
          <div>
            <div className="cb-pma-title">{t("cycleBot.dashboard.reportReadyTitle")}</div>
            <div className="cb-pma-sub">
              {t("cycleBot.dashboard.reportReadySubtitle")}
            </div>
          </div>
        </div>
      )}

      {alerts.length > 0 && (
        <>
          <div className="cb-section-title">{t("cycleBot.dashboard.diagnosticLastCycle")}</div>
          <div className="cb-alerts-list">
            {alerts.map((a, i) => (
              <AlertCard key={i} alert={a} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}
